import React from 'react';
import { makeStyles } from 'tss-react/mui';
import { IInsuredData } from '../..';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import { extractPersonsFromQuery, getInsuredLov } from './utils';
import { IInsuredLists } from './TravelPrincipalInsured';
import { ITravelAmendmentLovs } from '../../line-amendment/TravelAmendment';
import WithLinkFormField from '@components/form-fields/WithLinkFormField';
import AutocompleteFormField from '@components/form-fields/AutocompleteFormField';
import { useLazyQuery } from '@apollo/client';
import { fetchedPersonsWithPagination } from './queries';
import SelectFormField from '@components/form-fields/SelectFormField';
import { MAIN_THEME } from '@styles';

const useStyles = makeStyles()(() => ({
  container: {
    width: '100%',
    backgroundColor: MAIN_THEME.color.white98,
    border: `1px solid ${MAIN_THEME.color.white92}`,
    borderRadius: '4px',
    padding: '33px 23px 23px',
    marginBottom: '14px',
    position: 'relative',
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: `repeat(2, 49%)`,
    gap: '8px',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
  },
  label: {
    color: MAIN_THEME.color.black,
    margin: '0px!important',
    padding: '0px!important',
    marginBottom: '4px!important',
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
  },
  labelHeader: {
    fontSize:`${MAIN_THEME.fontSize.sm} !important`,
    lineHeight: '16px !important',
    fontFamily: `${MAIN_THEME.font.medium} !important`,
    marginBottom: '4px!important',
  },
  fullFieldRow: {
    gridColumn: '1 / -1',
    width: '100%',
  },
}));

interface InsuredRowProps {
  row: IInsuredData;
  rowIndex: number;
  insuredValues: IInsuredData[];
  insuredTouched: Record<string, boolean>[];
  insuredErrors: Record<string, string>[];
  onFieldUpdate: (
    field: string,
    value: string | string[],
    index: number
  ) => void;
  onFieldBlur: (field: string, index: number) => void;
  isDisabled: boolean;
  lists: IInsuredLists;
  lovs: ITravelAmendmentLovs;
  handleOpenDrawer: (type: string, index: number) => void;
  handleOpenAddressDrawer: (type: string, index: number) => void;
}

const InsuredRow: React.FC<InsuredRowProps> = ({
  row,
  rowIndex,
  insuredValues,
  insuredTouched,
  insuredErrors,
  onFieldUpdate,
  onFieldBlur,
  isDisabled,
  lists,
  lovs,
  handleOpenDrawer,
  handleOpenAddressDrawer,
}) => {
  const [fetchedPersonsLazy] = useLazyQuery(fetchedPersonsWithPagination);
  const { classes } = useStyles();

  return (
    <div className={classes.container} key={rowIndex}>
      <div className={classes.fieldRow}>
        <WithLinkFormField
          linkText={isDisabled ? '' : 'Add Person'}
          addSpace
          onLinkClick={() => {
            handleOpenDrawer('insured', rowIndex);
          }}
        >
          <AutocompleteFormField
            key="id"
            name="id"
            title="Insured*"
            placeholder="Insured"
            value={insuredValues[rowIndex].id}
            error={
              insuredTouched[rowIndex].id ? insuredErrors[rowIndex].id : ''
            }
            disabled={isDisabled}
            material
            onChange={(value) => {
              onFieldUpdate('id', value, rowIndex);
            }}
            fetchOptions={function (
              value,
              pagination,
              hasDefaultValue
            ): Promise<{
              options: Record<string, string>;
              totalCount: number;
            }> {
              const defaultValue = hasDefaultValue ? value : null;

              return fetchedPersonsLazy({
                variables: {
                  searchKeyword: hasDefaultValue ? null : value,
                  pagination,
                  selectedPersonID: defaultValue,
                },
              }).then((result) => {
                const options = extractPersonsFromQuery(
                  result.data?.Production?.queries
                    ?.GetAssignedPersonsWithPagination?.items
                );
                return {
                  options,
                  totalCount:
                    result.data?.Production?.queries
                      ?.GetAssignedPersonsWithPagination?.paging?.totalCount,
                };
              });
            }}
          />
        </WithLinkFormField>
        {row.isPrincipal ? (
          <WithLinkFormField
            linkText={isDisabled ? '' : 'Add Address'}
            addSpace
            onLinkClick={() => {
              handleOpenAddressDrawer('insured', rowIndex);
            }}
          >
            <SelectFormField
              key="address"
              name="address"
              title="Insured Address*"
              placeholder="Insured Address"
              value={insuredValues[rowIndex].address}
              error={
                insuredTouched[rowIndex].address
                  ? insuredErrors[rowIndex].address
                  : ''
              }
              material
              disabled={isDisabled}
              onChange={(v) => {
                onFieldUpdate('address', v, rowIndex);
              }}
              selectOptions={lists.insuredAddresses[rowIndex]}
            />
          </WithLinkFormField>
        ) : (
          <SelectFormField
            key="relation"
            name="relation"
            title="Relation*"
            placeholder="Relation"
            value={insuredValues[rowIndex].relation}
            error={
              insuredTouched[rowIndex].relation
                ? insuredErrors[rowIndex].relation
                : ''
            }
            onChange={(v) => onFieldUpdate('relation', v, rowIndex)}
            disabled={isDisabled}
            selectOptions={getInsuredLov(row, lovs, insuredValues)}
            material
          />
        )}
        <TextInputFormField
          key="cardNumber"
          name="cardNumber"
          title="Card Number"
          placeholder="Card Number"
          value={insuredValues[rowIndex].cardNumber}
          error={
            insuredTouched[rowIndex].cardNumber
              ? insuredErrors[rowIndex].cardNumber
              : ''
          }
          onBlur={() => onFieldBlur('cardNumber', rowIndex)}
          onChange={(v) =>
            onFieldUpdate('cardNumber', v.target.value, rowIndex)
          }
          disabled={isDisabled}
          material
        />

        <TextInputFormField
          key="exclusions"
          name="exclusions"
          title="Exclusions"
          placeholder="Exclusions"
          value={insuredValues[rowIndex].exclusions}
          error={
            insuredTouched[rowIndex].exclusions
              ? insuredErrors[rowIndex].exclusions
              : ''
          }
          onBlur={() => onFieldBlur('exclusions', rowIndex)}
          onChange={(v) =>
            onFieldUpdate('exclusions', v.target.value, rowIndex)
          }
          disabled={isDisabled}
          material
          fullWidth
        />

        <TextInputFormField
          key="remarks"
          name="remarks"
          title="Remarks"
          placeholder="Remarks"
          value={insuredValues[rowIndex].remarks}
          error={
            insuredTouched[rowIndex].remarks
              ? insuredErrors[rowIndex].remarks
              : ''
          }
          onBlur={() => onFieldBlur('remarks', rowIndex)}
          onChange={(v) => onFieldUpdate('remarks', v.target.value, rowIndex)}
          disabled={isDisabled}
          material
          fullWidth
        />
      </div>
    </div>
  );
};

export default InsuredRow;
